import React, { useState, useEffect } from "react";
import {
  TextField, Button, Grid, IconButton, InputAdornment, CircularProgress,
  Snackbar, Alert, useMediaQuery, Box, Typography, Paper, Container,
  Card, CardContent, Divider, Fade, Zoom, LinearProgress, Stepper,
  Step, StepLabel, Avatar, Chip, styled, alpha, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio
} from "@mui/material";
import InputMask from 'react-input-mask';
import {
  Visibility, VisibilityOff, Person, Email, Phone, Restaurant,
  LocationOn, Lock, CheckCircle, PersonAdd, Business
} from "@mui/icons-material";
import {
  collection, query, where, getDocs, setDoc, doc, or
} from "firebase/firestore";
import { db } from "../firebase-config";
import Collections from "../collections";
import appTheme from "../theme";

// **FIXED STYLED COMPONENTS WITH CORRECT THEME PROPERTIES**
const MainContainer = styled(Box)({
  minHeight: '100vh',
  background: appTheme.gradients?.surfaceLuxury || appTheme.colors.surfaceLuxury,
  padding: appTheme.spacing?.xl || '32px', // Fixed: using xl instead of lg
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const FormCard = styled(Card)({
  maxWidth: 900,
  width: '100%',
  borderRadius: appTheme.borderRadius?.rounded || '16px',
  boxShadow: appTheme.shadows?.strong || '0 8px 20px rgba(0,0,0,0.1)',
  border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: appTheme.gradients?.hero || appTheme.colors.primary,
  }
});

const HeaderSection = styled(Box)({
  background: appTheme.gradients?.cardLuxury || appTheme.colors.surfaceElevated,
  padding: appTheme.spacing?.xl || '32px',
  textAlign: 'center',
  position: 'relative',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: appTheme.borderRadius?.medium || '10px',
    backgroundColor: appTheme.colors?.surface || '#ffffff',
    transition: appTheme.transitions?.smooth || '300ms ease-out',
    '&:hover': {
      backgroundColor: appTheme.colors?.surfaceElevated || '#f7e7ce',
      '& fieldset': {
        borderColor: appTheme.colors?.primary || '#1a4d3a',
      },
    },
    '&.Mui-focused': {
      backgroundColor: appTheme.colors?.surface || '#ffffff',
      boxShadow: appTheme.shadows?.soft || '0 2px 6px rgba(0,0,0,0.06)',
      '& fieldset': {
        borderColor: appTheme.colors?.primary || '#1a4d3a',
        borderWidth: 2,
      },
    },
    '&.Mui-error': {
      '& fieldset': {
        borderColor: appTheme.colors?.error || '#c0392b',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: appTheme.typography?.fontWeights?.medium || 500,
    '&.Mui-focused': {
      color: appTheme.colors?.primary || '#1a4d3a',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: appTheme.spacing?.sm || '12px',
    fontWeight: appTheme.typography?.fontWeights?.medium || 500,
  },
});

const SubmitButton = styled(Button)({
  background: appTheme.gradients?.hero || appTheme.colors.primary,
  color: appTheme.colors?.champagne || '#f7e7ce',
  borderRadius: appTheme.borderRadius?.rounded || '16px',
  padding: `${appTheme.spacing?.md || '20px'} ${appTheme.spacing?.xl || '32px'}`,
  fontSize: appTheme.typography?.fontSizes?.large || '20px',
  fontWeight: appTheme.typography?.fontWeights?.bold || 700,
  textTransform: 'none',
  minWidth: 200,
  height: 56,
  boxShadow: appTheme.shadows?.medium || '0 4px 12px rgba(0,0,0,0.08)',
  transition: appTheme.transitions?.luxury || '400ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: appTheme.colors?.secondary || '#8b1538',
    boxShadow: appTheme.shadows?.strong || '0 8px 20px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: appTheme.colors?.borderSubtle || '#f4f6f7',
    color: appTheme.colors?.textMuted || '#8b7d73',
  },
});

const SectionCard = styled(Card)({
  borderRadius: appTheme.borderRadius?.medium || '10px',
  border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  boxShadow: appTheme.shadows?.whisper || '0 1px 3px rgba(0,0,0,0.04)',
  marginBottom: appTheme.spacing?.xl || '32px',
  overflow: 'hidden',
  transition: appTheme.transitions?.smooth || '300ms ease-out',
  '&:hover': {
    boxShadow: appTheme.shadows?.soft || '0 2px 6px rgba(0,0,0,0.06)',
  },
});

const SectionHeader = styled(Box)({
  background: alpha(appTheme.colors?.primary || '#1a4d3a', 0.05),
  padding: appTheme.spacing?.md || '20px',
  borderBottom: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  display: 'flex',
  alignItems: 'center',
  gap: appTheme.spacing?.sm || '12px',
});

const AddUsers = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    restaurantName: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [phoneType, setPhoneType] = useState('mobile');

  const isMobile = useMediaQuery("(max-width:600px)");

  // **CALCULATE FORM PROGRESS**
  useEffect(() => {
    const fields = Object.values(formData);
    const filledFields = fields.filter(field => field.trim() !== '').length;
    const progress = (filledFields / fields.length) * 100;
    setFormProgress(progress);
  }, [formData]);

  // **ENHANCED FORM VALIDATION**
  const validateForm = () => {
    let formErrors = {};

    if (!formData.name.trim()) {
      formErrors.name = "Full name is required";
    } else if (formData.name.trim().length < 2) {
      formErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      formErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      formErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      formErrors.phone = `${phoneType === 'mobile' ? 'Mobile' : 'Landline'} number is required`;
    } else if (!isValidUKPhone(formData.phone)) {
      formErrors.phone = phoneType === 'mobile'
        ? "Please enter a valid UK mobile number (07xxx xxx xxx or +44 7xxx xxx xxx)"
        : "Please enter a valid UK landline number (01xxx/02xxx/03xxx xxx xxx or +44 1xxx/2xxx/3xxx xxx xxx)";
    }


    if (!formData.restaurantName.trim()) {
      formErrors.restaurantName = "Restaurant name is required";
    } else if (formData.restaurantName.trim().length < 2) {
      formErrors.restaurantName = "Restaurant name must be at least 2 characters";
    }

    if (!formData.password) {
      formErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      formErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      formErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.address.trim()) {
      formErrors.address = "Address is required";
    } else if (formData.address.trim().length < 10) {
      formErrors.address = "Please provide a complete address";
    }

    return formErrors;
  };

 const isValidUKPhone = (phone) => {
  // Remove all spaces, hyphens, and other formatting
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

  if (phoneType === 'mobile') {
    // UK Mobile: 07XXXXXXXXX (11 digits total)
    const mobileRegex = /^07\d{9}$/;
    return mobileRegex.test(cleanPhone);
  } else {
    // UK Landline: Various formats
    // 020 XXXXXXXX (London - 11 digits)
    // 01XX XXXXXX or 01XXX XXXXX (Other areas - 11 digits)
    // 02X XXXXXXXX (Other major cities - 11 digits)
    const landlineRegex = /^0[123]\d{8,9}$/;
    return landlineRegex.test(cleanPhone);
  }
};



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const checkIfUserExists = async (email, phone) => {
    try {
      const q = query(
        collection(db, Collections.USERS),
        or(where("email", "==", email.toLowerCase()), where("phone", "==", phone))
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking user existence:", error);
      throw new Error("Failed to validate user data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setToast({
        open: true,
        message: "Please correct the errors in the form",
        severity: "error",
      });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const userExists = await checkIfUserExists(
        formData.email.toLowerCase(),
        formData.phone
      );

      if (userExists) {
        setToast({
          open: true,
          message: "A user with this email or phone number already exists",
          severity: "warning",
        });
        setLoading(false);
        return;
      }

      const newUser = {
        name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        restaurantName: formData.restaurantName.trim(),
        address: formData.address.trim(),
        createdAt: new Date().toISOString(),
        status: 'active',
      };

      const newDocRef = doc(collection(db, Collections.USERS));
      await setDoc(newDocRef, { ...newUser, id: newDocRef.id });

      setFormData({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        restaurantName: "",
        address: "",
      });

      setToast({
        open: true,
        message: "🎉 User added successfully! Welcome to the platform.",
        severity: "success",
      });

    } catch (error) {
      console.error("Error adding user:", error);
      setToast({
        open: true,
        message: "Failed to add user. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <FormCard>
            {/* **HEADER SECTION** */}
            <HeaderSection>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  background: appTheme.gradients?.hero || appTheme.colors.primary,
                  color: appTheme.colors?.champagne || '#f7e7ce',
                  fontSize: 40,
                }}
              >
                <PersonAdd />
              </Avatar>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: appTheme.typography?.fontWeights?.black || 900,
                  color: appTheme.colors?.primary || '#1a4d3a',
                  mb: 1,
                }}
              >
                Add New Restaurant Partner
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: appTheme.colors?.textSecondary || '#5d6d7e',
                  fontWeight: appTheme.typography?.fontWeights?.medium || 500,
                  mb: 3,
                }}
              >
                Create a new account for restaurant management
              </Typography>

              {/* **PROGRESS INDICATOR** */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Form Progress
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight="bold">
                    {Math.round(formProgress)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={formProgress}
                  sx={{
                    height: 8,
                    borderRadius: appTheme.borderRadius?.rounded || '16px',
                    backgroundColor: alpha(appTheme.colors?.primary || '#1a4d3a', 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: appTheme.borderRadius?.rounded || '16px',
                      background: appTheme.gradients?.hero || appTheme.colors.primary,
                    },
                  }}
                />
              </Box>

              <Chip
                icon={<CheckCircle />}
                label={`${Object.values(formData).filter(v => v.trim()).length} of 7 fields completed`}
                color="primary"
                variant="outlined"
                sx={{
                  borderColor: appTheme.colors?.primary || '#1a4d3a',
                  color: appTheme.colors?.primary || '#1a4d3a',
                  fontWeight: appTheme.typography?.fontWeights?.bold || 700,
                }}
              />
            </HeaderSection>

            {/* **FORM CONTENT** */}
            <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
              <form onSubmit={handleSubmit}>
                {/* **PERSONAL INFORMATION SECTION** */}
                <Zoom in timeout={600}>
                  <SectionCard>
                    <SectionHeader>
                      <Person sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: appTheme.typography?.fontWeights?.bold || 700,
                          color: appTheme.colors?.primary || '#1a4d3a',
                        }}
                      >
                        Personal Information
                      </Typography>
                    </SectionHeader>

                    <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.name}
                            helperText={errors.name}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Enter your full name"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.email}
                            helperText={errors.email}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Enter your email address"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
  <Box sx={{ mb: 2 }}>
    {/* Phone Type Selector */}
    <FormControl component="fieldset" sx={{ mb: 2 }}>
      <FormLabel
        component="legend"
        sx={{
          color: appTheme.colors?.primary || '#1a4d3a',
          fontWeight: appTheme.typography?.fontWeights?.semibold || 600,
          fontSize: '0.875rem'
        }}
      >
        Phone Type
      </FormLabel>
      <RadioGroup
        row
        value={phoneType}
        onChange={(e) => {
          setPhoneType(e.target.value);
          // Clear phone number when switching types
          setFormData(prev => ({ ...prev, phone: '' }));
          // Clear any phone errors
          if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: '' }));
          }
        }}
        sx={{ mt: 1 }}
      >
        <FormControlLabel
          value="mobile"
          control={<Radio sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />}
          label="Mobile"
        />
        <FormControlLabel
          value="landline"
          control={<Radio sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />}
          label="Landline"
        />
      </RadioGroup>
    </FormControl>

    {/* Dynamic Phone Input with Proper Masking */}
    <InputMask
      mask={phoneType === 'mobile' ? '09999 999999' : '09999 999999'}
      value={formData.phone}
      onChange={handleChange}
      maskChar=""
    >
      {() => (
        <StyledTextField
          label={`${phoneType === 'mobile' ? 'Mobile' : 'Landline'} Number`}
          name="phone"
          type="tel"
          fullWidth
          error={!!errors.phone}
          helperText={
            errors.phone ||
            (phoneType === 'mobile'
              ? "Format: 07123 456789"
              : "Format: 020 7946 0000 or 0113 496 0000"
            )
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
              </InputAdornment>
            ),
          }}
          placeholder={
            phoneType === 'mobile'
              ? "07123 456789"
              : "020 7946 0000"
          }
        />
      )}
    </InputMask>
  </Box>
</Grid>


                      </Grid>
                    </CardContent>
                  </SectionCard>
                </Zoom>

                {/* **BUSINESS INFORMATION SECTION** */}
                <Zoom in timeout={800}>
                  <SectionCard>
                    <SectionHeader>
                      <Business sx={{ color: appTheme.colors?.secondary || '#8b1538' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: appTheme.typography?.fontWeights?.bold || 700,
                          color: appTheme.colors?.secondary || '#8b1538',
                        }}
                      >
                        Business Information
                      </Typography>
                    </SectionHeader>

                    <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Restaurant Name"
                            name="restaurantName"
                            value={formData.restaurantName}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.restaurantName}
                            helperText={errors.restaurantName}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Restaurant sx={{ color: appTheme.colors?.secondary || '#8b1538' }} />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Enter restaurant name"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Business Address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={3}
                            error={!!errors.address}
                            helperText={errors.address}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                  <LocationOn sx={{ color: appTheme.colors?.secondary || '#8b1538' }} />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Enter complete business address"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </SectionCard>
                </Zoom>

                {/* **SECURITY SECTION** */}
                <Zoom in timeout={1000}>
                  <SectionCard>
                    <SectionHeader>
                      <Lock sx={{ color: appTheme.colors?.accent || '#d4af37' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: appTheme.typography?.fontWeights?.bold || 700,
                          color: appTheme.colors?.accent || '#d4af37',
                        }}
                      >
                        Security Settings
                      </Typography>
                    </SectionHeader>

                    <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.password}
                            helperText={errors.password || "Minimum 6 characters"}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: appTheme.colors?.accent || '#d4af37' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    sx={{ color: appTheme.colors?.accent || '#d4af37' }}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Create a secure password"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: appTheme.colors?.accent || '#d4af37' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    sx={{ color: appTheme.colors?.accent || '#d4af37' }}
                                  >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Confirm your password"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </SectionCard>
                </Zoom>

                {/* **SUBMIT SECTION** */}
                <Zoom in timeout={1200}>
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <SubmitButton
                      type="submit"
                      disabled={loading || formProgress < 100}
                      startIcon={loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <PersonAdd />}
                    >
                      {loading ? "Creating..." : "Create"}
                    </SubmitButton>

                    {formProgress < 100 && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 2,
                          color: appTheme.colors?.textSecondary || '#5d6d7e',
                          fontStyle: 'italic',
                        }}
                      >
                        Please complete all fields to create the account
                      </Typography>
                    )}
                  </Box>
                </Zoom>
              </form>
            </CardContent>
          </FormCard>
        </Fade>

        {/* **ENHANCED TOAST NOTIFICATION** */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            elevation={6}
            variant="filled"
            sx={{
              borderRadius: appTheme.borderRadius?.rounded || '16px',
              boxShadow: appTheme.shadows?.strong || '0 8px 20px rgba(0,0,0,0.1)',
              fontSize: appTheme.typography?.fontSizes?.medium || '18px',
              fontWeight: appTheme.typography?.fontWeights?.medium || 500,
              minWidth: 300,
              "& .MuiAlert-icon": {
                fontSize: "1.5rem",
              },
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </MainContainer>
  );
};

export default AddUsers;
